// FILE: components/notifications/NotificationDropdown.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { NOTIFICATION_CONFIGS } from "@/lib/notifications/types";
import { getNotifications, markAsRead, markAllAsRead, type NotificationRow } from "@/actions/notifications";

interface NotificationDropdownProps {
  onClose: () => void;
  onNavigate?: (page: string) => void;
  onUnreadCountChange?: (count: number) => void;
}

/** Tính thời gian tương đối */
function timeAgo(dateStr: string): string {
  const now = Date.now();
  const diff = now - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Vừa xong";
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ngày trước`;
  return new Date(dateStr).toLocaleDateString("vi-VN");
}

export default function NotificationDropdown({ onClose, onNavigate, onUnreadCountChange }: NotificationDropdownProps) {
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch on mount
  useEffect(() => {
    getNotifications(20).then((data) => {
      setItems(data);
      setLoading(false);
    });
  }, []);

  const unreadCount = items.filter((n) => !n.read).length;

  // Đánh dấu đã đọc khi click
  const handleClick = useCallback(async (item: NotificationRow) => {
    if (!item.read) {
      await markAsRead(item.id);
      setItems((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, read: true } : n))
      );
      onUnreadCountChange?.(unreadCount - 1);
    }
    // Tuỳ type → navigate
    if (item.type === "badge_earned") onNavigate?.("badges");
    else if (item.type === "exam_approved") onNavigate?.("creator-exams");
    else if (item.type === "new_follower") onNavigate?.("profile");
    onClose();
  }, [unreadCount, onClose, onNavigate, onUnreadCountChange]);

  // Đánh dấu tất cả đã đọc
  const handleMarkAll = useCallback(async () => {
    await markAllAsRead();
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    onUnreadCountChange?.(0);
  }, [onUnreadCountChange]);

  return (
    <div className="absolute top-full right-0 mt-2 w-72 rounded-xl border border-border bg-white shadow-[0_10px_40px_rgba(0,0,0,.12)] z-[9999] overflow-hidden animate-fade-slide">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <span className="text-[13px] font-bold text-[#0f172a]">Thông báo</span>
        {unreadCount > 0 && (
          <button onClick={handleMarkAll} className="text-[11px] text-teal-600 font-semibold hover:underline">
            Đọc tất cả
          </button>
        )}
      </div>

      {/* Body */}
      <div className="max-h-[320px] overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 text-center">
            <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="text-2xl mb-2">🔔</div>
            <p className="text-[13px] font-semibold text-[#0f172a] mb-1">Chưa có thông báo</p>
            <p className="text-[11px] text-[#94a3b8] leading-relaxed">
              Bạn sẽ nhận thông báo khi có<br />giao dịch, người theo dõi mới, huy hiệu...
            </p>
          </div>
        ) : (
          items.map((item) => {
            const config = NOTIFICATION_CONFIGS[item.type] ?? NOTIFICATION_CONFIGS.system;
            return (
              <button
                key={item.id}
                onClick={() => handleClick(item)}
                className={cn(
                  "w-full text-left px-4 py-3 border-b border-[#f8fafc] hover:bg-[#fafbfc] transition-colors flex items-start gap-3",
                  !item.read && "bg-teal-50/50"
                )}
              >
                {/* Icon */}
                <span className="text-base shrink-0 mt-0.5">{config.icon}</span>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={cn("text-[12px] leading-relaxed", item.read ? "text-[#64748b]" : "text-[#0f172a] font-medium")}>
                    {item.title}
                  </p>
                  {item.body && (
                    <p className="text-[11px] text-[#94a3b8] mt-0.5 truncate">{item.body}</p>
                  )}
                  <p className="text-[10px] text-[#94a3b8] mt-1">{timeAgo(item.created_at)}</p>
                </div>

                {/* Unread dot */}
                {!item.read && (
                  <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0 mt-1.5" />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      {items.length > 0 && (
        <div className="px-4 py-2.5 border-t border-border text-center">
          <button
            onClick={() => { onClose(); onNavigate?.("notifications"); }}
            className="text-[11px] text-teal-600 font-bold hover:underline"
          >
            Xem tất cả thông báo
          </button>
        </div>
      )}
    </div>
  );
}
